import { SearchIcon } from '@blueprinthq/joy'
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  VStack
} from '@chakra-ui/react'
import { FormErrorDisplay, SearchSelect } from '@components'
import {
  ArrowForwardIcon,
  CalendarIcon,
  MapMarkerIcon,
  RecurringIcon,
  VideoCamera
} from '@components/icons'
import { Field, type FieldProps, type FormikProps } from 'formik'
import moment from 'moment'
import React from 'react'
import {
  type ServiceTypeOption,
  useCalendarEvent
} from '../../context/calendar-event.context'
import { EVENT_SETTING_TELEHEALTH, type EventSetting } from '../../models'
import { BPDatePicker } from '../date-picker'
import { BPSelect } from '../select'
import { BPTimePicker } from '../time-picker'
import type { AddEditEventFormValues } from './add-edit-event-modal'

interface ClientSessionFormProps {
  onEditClient: () => void
  formikProps: FormikProps<AddEditEventFormValues>
  serviceTypeOptions: ServiceTypeOption[]
  settingOptions: { value: EventSetting; label: string }[]
  recurringOptions: { value: string; label: string }[]
}

export const ClientSessionForm: React.FC<ClientSessionFormProps> = ({
  onEditClient,
  formikProps,
  serviceTypeOptions,
  settingOptions,
  recurringOptions
}) => {
  const { currentEvent } = useCalendarEvent()

  if (!currentEvent) {
    return null
  }

  const { errors, setFieldValue, touched, values } = formikProps

  return (
    <Box py="16px">
      <Field name="eventType">
        {({ field }: FieldProps) => <Input type="hidden" {...field} />}
      </Field>
      <Field name="patientId">
        {({ field }: FieldProps) => <Input type="hidden" {...field} />}
      </Field>
      <Field name="subject">
        {({ field }: FieldProps) => <Input type="hidden" {...field} />}
      </Field>
      <InputGroup>
        <InputLeftElement>
          <SearchIcon name="search" />
        </InputLeftElement>
        <Input
          type="text"
          border="none"
          borderBottom="1px solid rgba(55, 60, 65, 0.14)"
          _focus={{
            outline: 'none',
            borderBottom: '1px solid var(--chakra-colors-primary)'
          }}
          fontSize="24px"
          readOnly
          onFocus={onEditClient}
          value={`${currentEvent.patient?.firstName} ${currentEvent.patient?.lastName}`}
        />
      </InputGroup>
      <Flex pt="16px" gap="12px" direction="column">
        <Box>
          <Field name="serviceType">
            {({ field }: FieldProps) => (
              <SearchSelect
                {...field}
                placeholder="Select service type"
                options={serviceTypeOptions}
                errors={errors.serviceType}
                onChange={(serviceType: ServiceTypeOption) => {
                  const duration = serviceTypeOptions.find(
                    option => option.value === serviceType.value
                  )?.duration
                  const endTime = moment(values.eventStartTime, 'hh:mm a').add(
                    duration,
                    'minutes'
                  )
                  setFieldValue(field.name, serviceType)
                  setFieldValue('eventEndTime', endTime.format('hh:mm a'))
                }}
                value={field.value}
              />
            )}
          </Field>
        </Box>
        <Box>
          <Text fontSize="12px" fontWeight="bold">
            Edit appointment details
          </Text>
        </Box>
        <VStack gap="4px" pb="12px">
          <HStack gap="8px" width="100%">
            <Field name="eventDate" type="date">
              {({ field }: FieldProps) => (
                <>
                  <Box padding="4px">
                    <CalendarIcon width="24px" height="24px" />
                  </Box>
                  <FormControl
                    isInvalid={!!errors.eventDate && !!touched.eventDate}
                    margin="0px"
                  >
                    <BPDatePicker
                      selected={field.value}
                      onChange={date => {
                        setFieldValue(field.name, date)
                      }}
                      errors={errors.eventDate}
                    />
                  </FormControl>
                </>
              )}
            </Field>
            <HStack>
              <Field name="eventStartTime" type="time">
                {({ field }: FieldProps) => (
                  <FormControl
                    isInvalid={
                      !!errors.eventStartTime && !!touched.eventStartTime
                    }
                  >
                    <BPTimePicker
                      {...field}
                      onChange={(value: string) => {
                        setFieldValue(field.name, value)
                      }}
                      errors={errors.eventStartTime}
                    />
                  </FormControl>
                )}
              </Field>
              <Box padding="4px">
                <ArrowForwardIcon />
              </Box>
              <Field name="eventEndTime" type="time">
                {({ field }: FieldProps) => (
                  <FormControl
                    isInvalid={!!errors.eventEndTime && !!touched.eventEndTime}
                  >
                    <BPTimePicker
                      {...field}
                      onChange={(value: string) => {
                        setFieldValue(field.name, value)
                      }}
                      errors={errors.eventEndTime}
                    />
                  </FormControl>
                )}
              </Field>
            </HStack>
          </HStack>
          <HStack gap="8px" alignItems="flex-start" width="100%">
            <Field name="setting">
              {({ field }: FieldProps) => (
                <>
                  <Box padding="12px 4px">
                    {field.value === 'in-person' && (
                      <MapMarkerIcon
                        width="24px"
                        height="24px"
                        fill="#A1A1A1"
                      />
                    )}
                    {field.value === EVENT_SETTING_TELEHEALTH && (
                      <VideoCamera width="24px" fill="#A1A1A1" />
                    )}
                  </Box>
                  <FormControl
                    isInvalid={!!errors.setting && !!touched.setting}
                  >
                    <BPSelect
                      selectedValue={field.value}
                      onChange={(value: string) =>
                        setFieldValue(field.name, value)
                      }
                      options={settingOptions}
                      errors={errors.setting}
                    />
                  </FormControl>
                </>
              )}
            </Field>
          </HStack>
          <HStack gap="8px" alignItems="flex-start" width="100%">
            <Field name="recurring">
              {({ field }: FieldProps) => (
                <>
                  <Box padding="12px 4px">
                    <RecurringIcon
                      width="24px"
                      height="24px"
                      fill={
                        field.value === 'no-repeat'
                          ? '#A1A1A1'
                          : 'var(--chakra-colors-primary)'
                      }
                    />
                  </Box>
                  <FormControl
                    isInvalid={!!errors.recurring && !!touched.recurring}
                    flex="2"
                  >
                    <BPSelect
                      selectedValue={field.value}
                      onChange={(value: string) =>
                        setFieldValue(field.name, value)
                      }
                      options={recurringOptions}
                      errors={errors.recurring}
                    />
                  </FormControl>
                </>
              )}
            </Field>
          </HStack>
          {formikProps.values.recurring !== 'no-repeat' && (
            <Field name="occurrenceCount">
              {({ field }: FieldProps) => (
                <FormControl
                  isInvalid={
                    !!errors.occurrenceCount && !!touched.occurrenceCount
                  }
                  flex="1"
                >
                  <FormLabel fontSize="12px" color="gray.600" mb="4px">
                    Repeat count
                  </FormLabel>
                  <NumberInput
                    {...field}
                    min={1}
                    max={100}
                    size="sm"
                    value={field.value || 1}
                    onChange={valueString =>
                      setFieldValue(field.name, parseInt(valueString) || 1)
                    }
                  >
                    <NumberInputField
                      placeholder="Count"
                      height="48px"
                      borderColor="rgba(55, 60, 65, 0.14)"
                      borderRadius="4px"
                    />
                    <NumberInputStepper borderColor="rgba(55, 60, 65, 0.14)">
                      <NumberIncrementStepper borderColor="rgba(55, 60, 65, 0.14)" />
                      <NumberDecrementStepper borderColor="rgba(55, 60, 65, 0.14)" />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              )}
            </Field>
          )}
        </VStack>
      </Flex>
      <FormErrorDisplay
        errors={errors}
        fieldNameMap={{
          serviceType: 'Service type',
          eventDate: 'Date',
          eventStartTime: 'Start time',
          eventEndTime: 'End time',
          setting: 'Setting',
          recurring: 'Recurring',
          occurrenceCount: 'Occurrence count',
          reminders: 'Reminders'
        }}
      />
    </Box>
  )
}
